import {Injectable, inject} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ConfirmComponent} from '../../common/modals/confirm/confirm.component';
import {Observable, Subject, zip} from 'rxjs';
import {I18nService} from './i18n.service';
import {first, mergeMap, takeUntil} from 'rxjs/operators';

/**
 * A service to help display modal dialogs.
 */
@Injectable({
  providedIn: 'root'
})
export class ConfirmModalService {
  private readonly modalService = inject(NgbModal);
  private readonly i18nService = inject(I18nService);


  /**
   * Opens a modal dialog.
   *
   * @param title The title config
   * @param title.i18nKey The i18n key for the title
   * @param title.data Any i18n data that needs to be used when getting the translation for the title
   * @param body The body config
   * @param body.i18nKey The i18n key for the body
   * @param body.data Any i18n data that needs to be used when getting the translation for the body
   * @param ariaLabelledBy The string ID of the element that contains the text to describe the modal window via ARIA
   * @param container The container that the modal is to be injected into.  Important to pass the closest possible component to the one that triggered the modal.
   *   Make sure the component itself doesn't also have a click event as that will mean all clicks inside the modal trigger that event.
   *   Should be used to ensure the modal is next to the opening element in the DOM. See: WCAG 2.4.3 - SCR26, SCR37
   *   It's optional because the source event is only meant to help us get the parent element.  Eg: If the source is a timer, then no specific element makes more sense than
   *   any other and the body tag will be used.
   * @returns {{ref: NgbModalRef; result$: Observable<boolean>}} Both a reference to the modal, and a boolean observable which is true if user accepts modal, false if they don't.
   */
  open(
    title: {i18nKey: string; data?: Record<string, unknown>},
    body: {i18nKey: string; data?: Record<string, unknown>},
    ariaLabelledBy: string,
    container: HTMLElement = document.body
  ): {ref: NgbModalRef; result$: Observable<boolean>} {

    const confirmComponentNgbModalRef: NgbModalRef = this.modalService.open(ConfirmComponent, {container: container, centered: true, ariaLabelledBy: ariaLabelledBy});

    const destroy$: Subject<void> = new Subject<void>();

    return {
      ref:     confirmComponentNgbModalRef,
      result$: zip( //Start by zipping the title/body translations together in a single response
        this.i18nService.getTranslation(title.i18nKey, title.data),
        this.i18nService.getTranslation(body.i18nKey, body.data)
      ).pipe(
        //Take the translations and set the modal values.  Return the modal's result as a simple boolean observable
        mergeMap(translations => new Observable<boolean>(subscriber => {
            confirmComponentNgbModalRef.componentInstance.title = translations[0];
            confirmComponentNgbModalRef.componentInstance.body = translations[1];
            confirmComponentNgbModalRef.result
              .then(() => subscriber.next(true))
              .catch(() => subscriber.next(false))
              .finally(() => {
                subscriber.complete();
                destroy$.next();
                destroy$.complete();
              });
          }).pipe(
            first()
          )
        ),
        //Closing the modal will signal that the translation subscription needs to close
        takeUntil(destroy$)
      )
    };
  }

}
